import { DEVELOPER_ONLY_MODULES } from "@/lib/config/lib/helper"
import { DEFAULT_PERMISSIONS } from "@/lib/utils"
import { RoleFormData, RolePermissions } from "@/types/types"
import { useEffect, useState } from "react"
import roleService from "../services/roleService"

let permissionsCache: RolePermissions | null = null
let permissionsRequest: Promise<RolePermissions | null> | null = null

const getPermissionsCached = async (fetcher: () => Promise<any>): Promise<RolePermissions | null> => {
  if (permissionsCache) {
    return permissionsCache
  }

  if (!permissionsRequest) {
    permissionsRequest = fetcher()
      .then(result => {
        permissionsCache = result || null
        return permissionsCache
      })
      .finally(() => {
        permissionsRequest = null
      })
  }

  return permissionsRequest
}

export const useRoleUpsert = (id: string | null, isEditMode: boolean) => {
  const { getRoleById, getAllPermissions } = roleService()

  const [formData, setFormData] = useState<RoleFormData>({
    name: "",
    description: "",
  })
  const [rolePermissions, setRolePermissions] = useState<RolePermissions>({})
  const [allPermissions, setAllPermissions] = useState<RolePermissions>({})

  const isDefaultPermission = (moduleKey: string, controller: string, action: string): boolean => {
    if (isEditMode) return false
    return !!DEFAULT_PERMISSIONS[moduleKey]?.controllers[controller]?.includes(action)
  }

  const buildDeveloperPermissions = (allPerms: RolePermissions): RolePermissions => {
    const result: RolePermissions = {}

    DEVELOPER_ONLY_MODULES.forEach(moduleKeyword => {
      Object.entries(allPerms).forEach(([moduleKey, moduleValue]: [string, any]) => {
        const cleanKey = moduleKey.replace("api::", "").replace("plugin::", "")

        if (cleanKey === moduleKeyword || moduleKey === moduleKeyword) {
          const controllers = moduleValue?.controllers || {}
          const enabledControllers: any = {}

          Object.entries(controllers).forEach(([controllerName, actions]: [string, any]) => {
            enabledControllers[controllerName] = {}
            Object.keys(actions).forEach(action => {
              enabledControllers[controllerName][action] = { enabled: true }
            })
          })

          result[moduleKey] = { controllers: enabledControllers }
        }
      })
    })

    return result
  }

  useEffect(() => {
    const initData = async () => {
      try {
        const perms = await getPermissionsCached(getAllPermissions)
        if (perms) {
          setAllPermissions(perms)
        }

        if (isEditMode && id) {
          const data = await getRoleById(id)
          if (data) {
            setFormData({ name: data.name, description: data.description })
            const devPerms = buildDeveloperPermissions(perms || {})
            setRolePermissions({
              ...(data.permissions || {}),
              ...devPerms,
            })
          }
        } else {
          const initialPerms: RolePermissions = {
            "users-permissions": {
              controllers: {
                auth: { connect: { enabled: true } },
                role: { findOne: { enabled: true } },
                user: {
                  findOne: { enabled: true },
                  me: { enabled: true },
                },
              },
            },
          }
          const devPerms = buildDeveloperPermissions(perms || {})
          setRolePermissions({ ...initialPerms, ...devPerms })
        }
      } catch (error) {
        console.error("Khoi tao du lieu loi:", error)
      }
    }
    initData()
  }, [id, isEditMode])

  const handlePermissionChange = (moduleKey: string, controller: string, action: string, enabled: boolean) => {
    if (isDefaultPermission(moduleKey, controller, action)) return

    setRolePermissions((prev: RolePermissions) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        controllers: {
          ...prev[moduleKey]?.controllers,
          [controller]: {
            ...prev[moduleKey]?.controllers?.[controller],
            [action]: { enabled },
          },
        },
      },
    }))
  }

  return {
    formData,
    setFormData,
    rolePermissions,
    allPermissions,
    handlePermissionChange,
    isDefaultPermission,
  }
}
